import { lighten } from '@mui/material'

import { Table } from 'components/Table'
import { useSort } from 'components/Table/Sort'
import { REPORT_SORT_FIELDS } from 'features/yieldbirdWrapperAgent/api/types'
import { useReports } from 'features/yieldbirdWrapperAgent/hooks/useReports'
import { isReportInProgress } from 'features/yieldbirdWrapperAgent/utils/isReportInProgress'
import { toListReportsPaging } from 'features/yieldbirdWrapperAgent/utils/toListReportsPaging'
import { DEFAULT_SORT } from './constants'
import { useColumns } from './useColumns'

import type { ReportRow } from './types'
import type { CSSObject, Theme } from '@mui/material'
import type { Row } from '@tanstack/react-table'
import type { FilterValue } from 'components/Filter'
import type { PaginationProps } from 'components/Table/Pagination'
import type { UsePagination } from 'components/Table/Pagination/types'
import type { Report, ReportSortField } from 'features/yieldbirdWrapperAgent/api/types'

const IN_PROGRESS_ROW_LIGHTEN: number = 0.9

type Props = {
  filters: FilterValue[]
  pagination: UsePagination
}

export const ReportsTable = ({ filters, pagination }: Props): React.JSX.Element => {
  const columns = useColumns()
  const { page, rowsPerPage } = pagination
  const { props: sortProps, sort } = useSort<ReportRow, ReportSortField>(DEFAULT_SORT, [
    ...REPORT_SORT_FIELDS,
  ])

  const { count, isLoading, reports } = useReports({
    ...toListReportsPaging({ page, rowsPerPage }),
    sortBy: sort.by,
    sortDirection: sort.direction,
    url: filters[0],
  })

  const paginationProps: PaginationProps = {
    count,
    ...pagination,
  }

  // `jobId` ties memoized cells to the report, `status` re-renders the row while polling
  const rows: ReportRow[] = reports.map(
    (report: Report): ReportRow => ({ ...report, id: `${report.jobId}_${report.status}` }),
  )

  const handleStyleRow = ({ original: { status } }: Row<ReportRow>, theme: Theme): CSSObject =>
    isReportInProgress(status)
      ? { backgroundColor: lighten(theme.palette.info.main, IN_PROGRESS_ROW_LIGHTEN) }
      : {}

  return (
    <Table<ReportRow>
      columns={columns}
      data={rows}
      loading={isLoading}
      meta={{ styleRow: handleStyleRow }}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
